import {
  NgFor,
  NgIf,
  NgOptimizedImage,
  NgSwitch,
  NgSwitchCase,
  NgSwitchDefault,
  NgTemplateOutlet
} from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  signal,
  untracked
} from "@angular/core";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { AbstractControl, FormBuilder, ReactiveFormsModule, Validators } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { select, Store } from "@ngrx/store";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { TextareaModule } from "src/app/common/components/form/textarea/textarea.module";
import { AnalyticsService } from "src/app/core/services/analytics/analytics.service";
import { DialogsService } from "src/app/core/services/dialogs.service";
import { EventService } from "src/app/core/services/event.service";
import { postContactData } from "src/app/ngrx/actions/app.actions";
import { RootState } from "src/app/ngrx/root-reducers";
import { selectRegionContactPerson, selectRegionServiceContactPerson } from "src/app/ngrx/selectors/app.selectors";
import { selectLaunchpadType } from "src/app/ngrx/selectors/overlay.selectors";
import { CardModule } from "../../../common/components/card/card.module";
import { DialogFrameModule } from "../../../common/components/dialog-frame/dialog-frame.module";
import { RadioModule } from "../../../common/components/form/radio/radio.module";
import { SelectModule } from "../../../common/components/form/select/select.module";
import { IconModule } from "src/app/shared/ui/icon/icon.module";
import { TabConfig, TabsComponent } from "../../../shared/ui/tabs/tabs.component";
import {
  NETZE_BW_FEEDBACK_TOPICS_OPTIONS,
  OCCURED_ERRORS_OPTIONS,
  PLATFORM_FEEDBACK_TOPICS_OPTIONS,
  SOCIAL_MEDIA_BUTTONS,
  TABS
} from "./contact-dialog.constants";
import { ContactDialogData } from "./contact-dialog-data.interface";

export type ContactTab = "contacts" | "platform" | "netzeBw";

@Component({
  selector: "tpm-contact-dialog",
  templateUrl: "contact-dialog.component.html",
  styleUrls: ["contact-dialog.component.scss"],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgIf,
    NgFor,
    NgSwitch,
    NgSwitchCase,
    NgSwitchDefault,
    NgTemplateOutlet,
    NgOptimizedImage,
    DialogFrameModule,
    TranslateModule,
    ReactiveFormsModule,
    CardModule,
    IconModule,
    RadioModule,
    SelectModule,
    TextareaModule,
    TabsComponent
  ]
})
export class ContactDialogComponent {
  private readonly dialogRef = inject(MatDialogRef<ContactDialogComponent>);
  private readonly data = inject<ContactDialogData | null>(MAT_DIALOG_DATA, { optional: true });
  private readonly dialogsService = inject(DialogsService);
  private readonly analytics = inject(AnalyticsService);
  private readonly eventService = inject(EventService);
  private readonly elementRef = inject(ElementRef);
  private readonly store = inject(Store<RootState>);
  private readonly fb = inject(FormBuilder);
  private readonly translate = inject(TranslateService);

  private readonly overlayType = toSignal(this.store.pipe(select(selectLaunchpadType)));
  private readonly contactPerson = toSignal(this.store.select(selectRegionContactPerson));
  private readonly serviceContactPerson = toSignal(this.store.select(selectRegionServiceContactPerson));

  readonly tab = signal<ContactTab>("contacts");
  readonly submitted = signal(false);

  readonly tabs: Array<TabConfig> = TABS;
  readonly socialMediaIcons = SOCIAL_MEDIA_BUTTONS;
  readonly occuredErrorsOptions = OCCURED_ERRORS_OPTIONS.map((option) => ({
    ...option,
    label: this.translate.instant(option.label)
  }));

  readonly form = this.fb.group({
    topic: [null, Validators.required],
    occurred: [null],
    text: ["", Validators.required]
  });

  readonly person = computed(() => {
    const person = this.overlayType() === "marketplace" ? this.serviceContactPerson() : this.contactPerson();
    const idx = person.address.search(/[\d]{5}/g);
    const postCode = idx > 0 ? person.address.slice(idx) : "";
    const address = idx > 0 ? person.address.slice(0, idx) : person.address;
    return { ...person, postCode, address };
  });

  readonly submitButtonLabel = computed(() =>
    !this.submitted() && this.tab() !== "contacts" ? "CONTACT_DIALOG.feedbackForm.sendButtonLabel" : ""
  );
  readonly feedbackFormHeader = computed(() => `CONTACT_DIALOG.feedbackForm.header_${this.tab()}`);
  readonly feedbackFormDescription = computed(() => `CONTACT_DIALOG.feedbackForm.description_${this.tab()}`);

  readonly feedbackTopicsOptions = computed(() => {
    const options = this.tab() === "platform" ? PLATFORM_FEEDBACK_TOPICS_OPTIONS : NETZE_BW_FEEDBACK_TOPICS_OPTIONS;

    return options.map((option) => ({
      ...option,
      label: this.translate.instant(option.label)
    }));
  });

  get topicControl(): AbstractControl {
    return this.form.get("topic");
  }

  get textControl(): AbstractControl {
    return this.form.get("text");
  }

  constructor() {
    let skipTabTracking = !!this.data?.skipInitialTabTracking;

    effect(() => {
      const tab = this.tab();

      if (skipTabTracking) {
        skipTabTracking = false;
        return;
      }

      untracked(() => {
        this.analytics.trackContactTabClick(tab, this.data?.analyticsAction);
      });
    });

    this.topicControl.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => {
      if (this.topicControl.value === "errors") {
        this.form.get("occurred").addValidators([Validators.required]);
      } else {
        this.form.get("occurred").removeValidators(Validators.required);
        this.form.get("occurred").patchValue(null);
      }

      this.form.get("occurred").updateValueAndValidity();
    });

    this.eventService
      .fromClick(this.elementRef, "data-protection-link")
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        this.dialogRef.close();
        this.dialogsService.openPolicyDialog("dataProtection");
      });
  }

  onTabChange(tab: ContactTab): void {
    this.tab.set(tab);
    this.formReset();
  }

  onSubmit(): void {
    if (this.form.invalid) {
      return;
    }

    this.store.dispatch(
      postContactData({
        form: this.form.getRawValue(),
        requestType: this.tab()
      })
    );

    this.submitted.set(true);
    this.tab.set(null);
    this.formReset();
  }

  formReset(): void {
    this.form.patchValue({
      topic: null,
      occurred: null,
      text: ""
    });
    this.form.markAsUntouched();
  }
}
